import { Controller } from '@hotwired/stimulus'
import { polite } from 'helpers/live_region_helper'
import { tableSelectionChanged } from '../../../helpers/custom_events_helper'

const INTERACTIVE_ELEMENTS = 'a, button, input, label, select, textarea, [role="button"]'

export default class extends Controller {
  static targets = ['supercheckbox', 'rowCheckbox']

  navigate(event) {
    if (event.target.closest(INTERACTIVE_ELEMENTS)) return

    const row = event.target.closest('[role="row"]')
    if (!row) return

    const wrapper = row.querySelector('[data-table-row-click]')
    if (!wrapper) return

    // Find the first interactive element to forward the click to. We use querySelector
    // rather than `firstElementChild` so callers can wrap the target in a component (e.g.
    // `UI::ModalComponent`) whose trigger button is nested inside.
    const target = wrapper.querySelector(INTERACTIVE_ELEMENTS)
    if (!target) return

    // For anchors, honor cmd/ctrl-click → open in new tab. The default browser behavior
    // only fires for real user clicks; flip target=_blank to get the same effect for the
    // synthetic click below, then restore.
    const isAnchor = target.tagName === 'A'
    if (isAnchor && (event.ctrlKey || event.metaKey)) {
      const originalTarget = target.getAttribute('target')
      target.setAttribute('target', '_blank')
      target.click()
      if (originalTarget === null) {
        target.removeAttribute('target')
      } else {
        target.setAttribute('target', originalTarget)
      }
      return
    }

    target.click()
  }

  toggleRow(event) {
    const row = event.target.closest('[role="row"]')
    if (!row) return

    const selected = event.target.checked
    row.classList.toggle('table__row--selected', selected)
    this._updateSupercheckbox()
    this._fireSelectionChanged()

    const header = row.querySelector('[role="rowheader"]')
    if (header) {
      polite.setContent(`Row ${header.textContent.trim()} ${selected ? 'selected' : 'unselected'}`)
    }
  }

  toggleAll(event) {
    this._setAll(event.target.checked)
  }

  deselectAll() {
    this._setAll(false)
  }

  get selectionData() {
    return this.rowCheckboxTargets
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => JSON.parse(checkbox.dataset.selectionValue))
  }

  addSelectionChangeListener(callback) {
    this.element.addEventListener('table:selectionChanged', callback)
  }

  _setAll(checked) {
    this.rowCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = checked
      const row = checkbox.closest('[role="row"]')
      if (row) row.classList.toggle('table__row--selected', checked)
    })

    if (this.hasSupercheckboxTarget) {
      this._updateSupercheckboxValue(checked)
    }

    this._fireSelectionChanged()
    polite.setContent(checked ? 'All rows selected' : 'All rows unselected')
  }

  _fireSelectionChanged() {
    tableSelectionChanged(this.element, { selection: this.selectionData })
  }

  _updateSupercheckbox() {
    const total = this.rowCheckboxTargets.length
    const checked = this.rowCheckboxTargets.filter((cb) => cb.checked).length

    this._updateSupercheckboxValue(total > 0 && checked === total)
  }

  _updateSupercheckboxValue(checked) {
    if (!this.hasSupercheckboxTarget) return

    this.supercheckboxTarget.checked = checked
    const labelContainer = this.supercheckboxTarget.previousElementSibling
    const newLabelText = checked ? 'Unselect All' : 'Select All'
    if (labelContainer.textContent !== newLabelText) {
      labelContainer.textContent = newLabelText
    }
  }
}
